import type { BillingSubscriptionPanel } from '@repo/contracts';

/**
 * Money/date formatting and subscription-status derivation for the Pro card.
 *
 * Ported from flotoapps' `manage_billing` so both products describe the same
 * Stripe state in the same words — a subscription that is `active` but has
 * `cancelAtPeriodEnd` reads "Ends soon" here exactly as it does there.
 */

export const formatMoney = (
  amountInCents: number | null | undefined,
  currency: string,
): string => {
  if (amountInCents === null || amountInCents === undefined) return '—';
  try {
    return new Intl.NumberFormat('en-US', {
      currency: (currency || 'usd').toUpperCase(),
      style: 'currency',
    }).format(amountInCents / 100);
  } catch {
    return `$${(amountInCents / 100).toFixed(2)}`;
  }
};

/** A short date, or empty when there is none, so it can be interpolated into
 *  a sentence that then omits the date entirely. */
export const formatShortDate = (iso: string | null | undefined): string => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

/**
 * The subscription still works but will not renew.
 *
 * Three ways Stripe expresses that: an outright `canceled` status, the
 * `cancel_at_period_end` flag, or — the case that looks fine until you read
 * it twice — an `active` subscription with no projected total, which is what
 * an upcoming invoice lookup returns once nothing more is scheduled.
 */
export const isEndingWithoutRenewal = (
  panel: BillingSubscriptionPanel,
): boolean =>
  panel.subscriptionStatus === 'canceled' ||
  panel.cancelAtPeriodEnd ||
  (panel.subscriptionStatus === 'active' &&
    panel.projectedTotal == null &&
    Boolean(panel.subscriptionPeriodEnd));

export interface SubscriptionBadge {
  label: string;
  className: string;
}

export const getSubscriptionBadge = (
  panel: BillingSubscriptionPanel,
): SubscriptionBadge | null => {
  if (panel.subscriptionStatus === 'canceled') {
    return { className: 'bg-tag-minor text-text-secondary', label: 'Canceled' };
  }

  if (
    panel.subscriptionStatus === 'past_due' ||
    panel.subscriptionStatus === 'unpaid'
  ) {
    return {
      className: 'bg-tag-critical text-critical',
      label: 'Payment issue',
    };
  }

  if (panel.subscriptionStatus === 'trialing') {
    return { className: 'bg-info-soft text-info', label: 'Trial' };
  }

  if (panel.subscriptionStatus === 'incomplete') {
    return { className: 'bg-tag-major text-tag-major-ink', label: 'Pending' };
  }

  if (isEndingWithoutRenewal(panel)) {
    return { className: 'bg-tag-major text-tag-major-ink', label: 'Ends soon' };
  }

  if (panel.subscriptionStatus === 'active') {
    return { className: 'bg-tag-success text-success-ink', label: 'Active' };
  }

  return null;
};

export interface SubscriptionMessage {
  text: string;
  tone: 'warning' | 'neutral' | 'muted';
}

export const getSubscriptionMessage = (
  panel: BillingSubscriptionPanel,
): SubscriptionMessage | null => {
  if (panel.subscriptionStatus === 'canceled') {
    return { text: 'This subscription has been canceled.', tone: 'muted' };
  }

  if (panel.subscriptionStatus === 'past_due') {
    return { text: 'Please update your payment method.', tone: 'warning' };
  }

  if (panel.subscriptionStatus === 'unpaid') {
    return {
      text: 'Your subscription is at risk. Update payment to avoid interruption.',
      tone: 'warning',
    };
  }

  if (panel.subscriptionStatus === 'incomplete') {
    return { text: 'Awaiting payment confirmation.', tone: 'neutral' };
  }

  if (panel.subscriptionStatus === 'trialing') {
    const date = formatShortDate(panel.subscriptionPeriodEnd);
    return {
      text: date ? `Free trial — ends on ${date}.` : 'Free trial active.',
      tone: 'neutral',
    };
  }

  if (isEndingWithoutRenewal(panel)) {
    const date = formatShortDate(panel.subscriptionPeriodEnd);
    return {
      text: date
        ? `${panel.kind === 'pro' ? 'Pro' : 'Plan'} until ${date}.`
        : 'This subscription will end at the close of the current billing period.',
      tone: 'neutral',
    };
  }

  if (panel.subscriptionPeriodEnd) {
    return {
      text: `Next renewal: ${formatShortDate(panel.subscriptionPeriodEnd)}`,
      tone: 'muted',
    };
  }

  return null;
};
